import { ApiError } from './errors';
import { includesTr } from './format';
import * as seed from './mockData';
import type {
  AiSettings,
  AuditEvent,
  Contact,
  Conversation,
  Doc,
  Faq,
  Message,
  PanelConfig,
  PanelSettings,
  TestModeState,
  TestPreviewResult,
  Ticket,
  User
} from './types';

/**
 * VITE_MOCK=1 modunda tüm ağ çağrılarını karşılayan bellek içi API.
 * Amaç: backend olmadan panelin uçtan uca gezilebilmesi; guard/test modu
 * davranışının da UI'da gerçekçi görünmesi (izin listesi dışına gönderim engellenir).
 */

interface MockDb {
  conversations: Conversation[];
  messages: Message[];
  contacts: Contact[];
  tickets: Ticket[];
  faqs: Faq[];
  docs: Doc[];
  audit: AuditEvent[];
  ai: AiSettings;
  settings: PanelSettings;
  testMode: TestModeState;
  currentUser: User | null;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const db: MockDb = {
  conversations: clone(seed.conversations),
  messages: clone(seed.messages),
  contacts: clone(seed.contacts),
  tickets: clone(seed.tickets),
  faqs: clone(seed.faqs),
  docs: clone(seed.docs),
  audit: clone(seed.auditEvents),
  ai: clone(seed.aiSettings),
  settings: clone(seed.panelSettings),
  testMode: clone(seed.testModeState),
  currentUser: null
};

let pendingTwoFaEmail: string | null = null;

/** GET/PUT /api/settings — panel yapılandırması (mock). */
const panelConfig: PanelConfig = {
  panelName: 'Destek Merkezi',
  supportEmail: 'destek@softwareeni.com',
  timezone: 'Europe/Istanbul',
  businessHoursStart: '09:00',
  businessHoursEnd: '18:00',
  testRecipients: ['+905518885886']
};

/** TOTP kurulum durumu (mock): setup → bekleyen sır, enable → etkin. */
const totpMock: { secret: string | null; enabled: boolean } = { secret: null, enabled: false };

/** Mock WA bağlantı durumu — QR ekranının backend'siz denenebilmesi için. */
const waMock: { status: string; qr: string | null; pairingCode: string | null; phone: string | null; connectedAt: string | null } = {
  status: 'disconnected',
  qr: null,
  pairingCode: null,
  phone: null,
  connectedAt: null
};

function latency(): Promise<void> {
  const ms = 80 + Math.round(Math.random() * 120);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function fail(code: string, message: string, status = 400): never {
  throw new ApiError(code, message, status);
}

function nowIso(): string {
  return new Date().toISOString();
}

let seq = 1000;
function nextId(prefix: string): string {
  seq += 1;
  return prefix + '-' + seq;
}

function digits(value: string): string {
  return String(value || '').replace(/[^0-9]/g, '');
}

/** +90 5xx xxx xx xx → E.164; geçersizse null. */
function normalizePhone(raw: string): string | null {
  let d = digits(raw);
  if (d.length === 12 && d.startsWith('90')) d = d.slice(2);
  d = d.replace(/^0+/, '');
  if (d.length !== 10 || d[0] !== '5') return null;
  return '+90' + d;
}

function activeAllowlist(): string[] {
  return db.testMode.allowlist.filter((entry) => entry.active).map((entry) => entry.phone);
}

function pushAudit(action: string, target: string, result: AuditEvent['result'], meta: string | null): void {
  db.audit.unshift({
    id: nextId('a'),
    at: nowIso(),
    actor: db.currentUser ? db.currentUser.name : 'Sistem',
    action,
    target,
    ip: '127.0.0.1',
    result,
    meta
  });
}

function contactForConversation(conversation: Conversation): Contact {
  const existing = db.contacts.find((contact) => contact.phone === conversation.phone);
  if (existing) return existing;

  const created: Contact = {
    id: conversation.contactId,
    phone: conversation.phone,
    name: conversation.name,
    optIn: false,
    optInAt: null,
    createdAt: conversation.lastAt,
    labels: conversation.labels,
    notes: '',
    orders: [],
    ticketCount: 0,
    lastSeenAt: conversation.lastAt,
    blocked: false
  };
  db.contacts.push(created);
  return created;
}

function filterConversations(filter: string, search: string): Conversation[] {
  let list = db.conversations.slice();
  switch (filter) {
    case 'mine':
      list = list.filter((item) => item.assignedTo === (db.currentUser ? db.currentUser.id : ''));
      break;
    case 'waiting':
      list = list.filter((item) => item.status !== 'closed' && item.waitingSince !== null);
      break;
    case 'ai':
      list = list.filter((item) => item.aiState === 'ai');
      break;
    case 'test':
      list = list.filter((item) => item.isTest);
      break;
    default:
      break;
  }
  if (search) {
    list = list.filter(
      (item) =>
        includesTr(item.name || '', search) ||
        includesTr(item.phone, search) ||
        includesTr(item.lastMessage, search)
    );
  }
  list.sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  return list;
}

function findConversation(id: string): Conversation {
  const found = db.conversations.find((item) => item.id === id);
  if (!found) fail('NOT_FOUND', 'Konuşma bulunamadı.', 404);
  return found;
}

function appendMessage(conversation: Conversation, message: Message): Message {
  db.messages.push(message);
  conversation.lastMessage = message.isNote ? conversation.lastMessage : message.body;
  conversation.lastAt = message.at;
  return message;
}

/** Guard: test/PILOT modunda izin listesi dışına gönderim engellenir (PLAN §6.2). */
function guardOutbound(conversation: Conversation): { allowed: boolean; reason: string } {
  if (db.testMode.runtimeMode === 'LIVE') return { allowed: true, reason: 'LIVE' };
  const list = activeAllowlist();
  if (list.length === 0) return { allowed: false, reason: 'ALLOWLIST_EMPTY' };
  if (list.includes(conversation.phone)) return { allowed: true, reason: 'allowlist' };
  return { allowed: false, reason: 'NOT_ALLOWLISTED' };
}

function upsertDocReady(doc: Doc): void {
  setTimeout(() => {
    const target = db.docs.find((item) => item.id === doc.id);
    if (!target || target.status === 'failed') return;
    target.status = 'ready';
    target.pages = 18;
    target.chunks = 42;
    target.proposedFaqs = 2;
    target.indexedAt = nowIso();
  }, 4000);
}

export async function mockRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  await latency();

  const url = new URL(path, 'http://mock.local');
  const route = method.toUpperCase() + ' ' + url.pathname;
  const input = (body ?? {}) as Record<string, unknown>;
  const query = url.searchParams;

  // ---------- Auth ----------
  if (route === 'GET /api/auth/me') {
    if (!db.currentUser) fail('AUTH_REQUIRED', 'Oturum bulunamadı.', 401);
    return db.currentUser as T;
  }

  if (route === 'POST /api/auth/login') {
    const mode = String(input.mode || 'sso');
    if (mode === 'sso') {
      db.currentUser = input.role === 'agent' ? clone(seed.MOCK_AGENT) : clone(seed.MOCK_OWNER);
      pushAudit('auth.login', 'selfportal', 'ok', 'SSO');
      return db.currentUser as T;
    }

    // Break-glass: gerçek API gibi önce parola doğrulanır, sonra 2FA bileti döner.
    const email = String(input.email || '').trim();
    const password = String(input.password || '');
    if (!email.includes('@') || password.length < 4) {
      fail('INVALID_CREDENTIALS', 'E-posta veya şifre hatalı.', 401);
    }
    pendingTwoFaEmail = email;
    return { twoFactorRequired: true, ticket: 'mock-ticket', csrfToken: 'mock-csrf', expiresInSec: 300 } as T;
  }

  if (route === 'POST /api/auth/2fa/verify') {
    const code = String(input.code || '').trim();
    if (code !== '123456') {
      fail('INVALID_2FA_CODE', 'Doğrulama kodu geçersiz. (demo kodu: 123456)', 401);
    }
    const email = pendingTwoFaEmail || 'tolga@softwareeni.com';
    db.currentUser = clone(email.startsWith('selin') ? seed.MOCK_AGENT : seed.MOCK_OWNER);
    pendingTwoFaEmail = null;
    pushAudit('auth.login', 'break-glass', 'ok', 'TOTP');
    return db.currentUser as T;
  }

  if (route === 'POST /api/auth/totp/setup') {
    totpMock.secret = 'JBSWY3DPEHPK3PXP';
    return {
      secret: totpMock.secret,
      otpauthUrl: 'otpauth://totp/Destek%20Merkezi:tolga@softwareeni.com?secret=' + totpMock.secret + '&issuer=Destek%20Merkezi',
      issuer: 'Destek Merkezi'
    } as T;
  }

  if (route === 'POST /api/auth/totp/enable') {
    const code = String(input.code || '').trim();
    if (code !== '123456') fail('INVALID_2FA_CODE', 'Doğrulama kodu geçersiz. (demo: 123456)', 401);
    totpMock.enabled = true;
    return { enabled: true, recoveryCodes: Array.from({ length: 10 }, (_, i) => 'KURTARMA-' + String(i + 1).padStart(2, '0') + '-ABCD') } as T;
  }

  if (route === 'POST /api/auth/recovery-codes') {
    const code = String(input.code || '').trim();
    if (code !== '123456') fail('INVALID_2FA_CODE', 'Doğrulama kodu geçersiz. (demo: 123456)', 401);
    return { recoveryCodes: Array.from({ length: 10 }, (_, i) => 'YENI-' + String(i + 1).padStart(2, '0') + '-WXYZ') } as T;
  }

  if (route === 'POST /api/auth/logout') {
    db.currentUser = null;
    return undefined as T;
  }

  // Auth gerektiren uçlar
  if (!db.currentUser) {
    fail('AUTH_REQUIRED', 'Oturum bulunamadı.', 401);
  }

  // ---------- WA durumu ----------
  if (route === 'GET /api/wa/status') {
    return {
      connected: true,
      device: 'Chrome 142 · Ubuntu 24.04',
      connectedAt: new Date(Date.now() - 42 * 60_000).toISOString(),
      browserIdentity: 'ubuntu',
      mode: db.testMode.runtimeMode
    } as T;
  }

  if (route === 'GET /api/wa/status') {
    return {
      status: waMock.status,
      connected: waMock.status === 'connected',
      qr: waMock.qr,
      pairingCode: waMock.pairingCode,
      phoneMasked: waMock.phone,
      connectedAt: waMock.connectedAt,
      device: 'Chrome 142 · Ubuntu 24.04',
      browserIdentity: 'ubuntu',
      mode: db.testMode.runtimeMode
    } as T;
  }

  if (route === 'POST /api/wa/pair') {
    const method = String(input.method || 'qr');
    if (method === 'code') {
      waMock.status = 'pairing';
      waMock.qr = null;
      waMock.pairingCode = 'ABCD-1234';
    } else {
      waMock.status = 'pairing';
      waMock.pairingCode = null;
      waMock.qr = '2@' + Math.random().toString(36).slice(2) + '-mock-qr';
    }
    pushAudit('wa.pair_start', method, 'ok', null);
    return { ok: true } as T;
  }

  if (route === 'POST /api/wa/logout') {
    waMock.status = 'disconnected';
    waMock.qr = null;
    waMock.pairingCode = null;
    waMock.phone = null;
    waMock.connectedAt = null;
    pushAudit('wa.logout', 'device-1', 'ok', null);
    return { ok: true } as T;
  }

  if (route === 'POST /api/wa/reconnect') {
    pushAudit('wa.reconnect', 'device-1', 'ok', 'manuel');
    waMock.status = 'connected';
    waMock.connectedAt = nowIso();
    return {
      status: waMock.status,
      connected: true,
      qr: null,
      pairingCode: null,
      phoneMasked: waMock.phone,
      device: 'Chrome 142 · Ubuntu 24.04',
      browserIdentity: 'ubuntu',
      connectedAt: waMock.connectedAt,
      mode: db.testMode.runtimeMode
    } as T;
  }

  // ---------- Medya ----------
  if (route === 'POST /api/media/upload') {
    const name = String(input.name || 'dosya');
    const mime = String(input.mime || 'application/octet-stream');
    const id = nextId('media');
    const placeholder =
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="160"><rect width="240" height="160" fill="#eef0ee"/><text x="120" y="86" font-family="sans-serif" font-size="12" fill="#5e6b75" text-anchor="middle">mock görsel</text></svg>'
      );
    return {
      id,
      url: mime.startsWith('image/') ? placeholder : '/api/media/' + id + '/' + encodeURIComponent(name),
      mime,
      size: Number(input.size || 0),
      name
    } as T;
  }

  // ---------- Gelen kutusu ----------
  if (route === 'GET /api/conversations') {
    const items = filterConversations(String(query.get('filter') || 'all'), String(query.get('q') || ''));
    return { items: clone(items), nextCursor: null } as T;
  }

  const conversationMatch = /^\/api\/conversations\/([^/]+)(\/.*)?$/.exec(url.pathname);
  if (conversationMatch) {
    const id = conversationMatch[1];
    const tail = conversationMatch[2] || '';
    const conversation = findConversation(id);

    if (route.startsWith('GET ') && tail === '') {
      const list = db.messages
        .filter((message) => message.conversationId === id)
        .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
      return {
        conversation: clone(conversation),
        messages: clone(list),
        contact: clone(contactForConversation(conversation))
      } as T;
    }

    if (tail === '/messages' && method.toUpperCase() === 'POST') {
      const text = String(input.body || '').trim();
      if (!text) fail('VALIDATION', 'Mesaj boş olamaz.');
      const guard = guardOutbound(conversation);
      const media = (input.media ?? null) as { url?: string; mime?: string; name?: string } | null;
      const message: Message = {
        id: nextId('m'),
        conversationId: id,
        author: 'agent',
        kind: media ? (String(media.mime || '').startsWith('image/') ? 'image' : 'document') : 'text',
        body: text,
        at: nowIso(),
        status: guard.allowed ? 'sent' : 'blocked',
        blockedReason: guard.allowed ? undefined : 'TEST_MODE_BLOCKED',
        mediaName: media?.name,
        mediaUrl: media?.url,
        mediaMime: media?.mime
      };
      if (!guard.allowed) {
        db.testMode.counters.blocked24h += 1;
        pushAudit('test_mode.blocked_send', conversation.phone, 'denied', guard.reason);
      } else {
        db.testMode.counters.outboundToday += 1;
        db.testMode.counters.allowed24h += 1;
      }
      return clone(appendMessage(conversation, message)) as T;
    }

    if (tail === '/notes' && method.toUpperCase() === 'POST') {
      const text = String(input.body || '').trim();
      if (!text) fail('VALIDATION', 'Not boş olamaz.');
      const message: Message = {
        id: nextId('m'),
        conversationId: id,
        author: 'agent',
        kind: 'text',
        body: text,
        at: nowIso(),
        isNote: true
      };
      return clone(appendMessage(conversation, message)) as T;
    }

    if (tail === '/read' && method.toUpperCase() === 'POST') {
      conversation.unread = 0;
      return undefined as T;
    }

    if (tail === '/ai/takeover' && method.toUpperCase() === 'POST') {
      conversation.aiState = 'human';
      pushAudit('conversation.takeover', id, 'ok', 'ai→human');
      return clone(conversation) as T;
    }

    if (tail === '/ai/resume' && method.toUpperCase() === 'POST') {
      conversation.aiState = 'ai';
      pushAudit('conversation.resume', id, 'ok', 'human→ai');
      return clone(conversation) as T;
    }

    if (tail === '/assign' && method.toUpperCase() === 'POST') {
      conversation.assignedTo = input.userId ? String(input.userId) : null;
      return clone(conversation) as T;
    }
  }

  // ---------- Kişi 360 ----------
  if (route === 'GET /api/contacts') {
    // Tüm konuşmalar için kişi kaydı olduğundan emin ol (panelden ulaşılabilirlik).
    for (const conversation of db.conversations) contactForConversation(conversation);
    const search = String(query.get('q') || '');
    let items = db.contacts.slice();
    if (search) {
      items = items.filter(
        (contact) => includesTr(contact.name || '', search) || includesTr(contact.phone, search) || contact.labels.some((label) => includesTr(label, search))
      );
    }
    items.sort((a, b) => new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime());
    return { items: clone(items), nextCursor: null } as T;
  }

  const contactMatch = /^\/api\/contacts\/([^/]+)(\/.*)?$/.exec(url.pathname);
  if (contactMatch) {
    const id = contactMatch[1];
    const tail = contactMatch[2] || '';
    const contact = db.contacts.find((item) => item.id === id);
    if (!contact) fail('NOT_FOUND', 'Kişi bulunamadı.', 404);

    if (method.toUpperCase() === 'GET') return clone(contact) as T;
    if (tail === '/opt-out') {
      contact.optIn = false;
      pushAudit('contact.opt_out', contact.phone, 'ok', null);
      return clone(contact) as T;
    }
    if (tail === '/block') {
      contact.blocked = true;
      pushAudit('contact.block', contact.phone, 'ok', null);
      return clone(contact) as T;
    }
    if (method.toUpperCase() === 'PATCH') {
      contact.notes = String(input.notes || '');
      return clone(contact) as T;
    }
  }

  // ---------- Ticket ----------
  if (route === 'GET /api/tickets') {
    const status = String(query.get('status') || 'all');
    const items = status === 'all' ? db.tickets.slice() : db.tickets.filter((item) => item.status === status);
    return { items: clone(items), nextCursor: null } as T;
  }

  const ticketMatch = /^\/api\/tickets\/([^/]+)(\/.*)?$/.exec(url.pathname);
  if (ticketMatch) {
    const id = ticketMatch[1];
    const tail = ticketMatch[2] || '';
    const ticket = db.tickets.find((item) => item.id === id);
    if (!ticket) fail('NOT_FOUND', 'Ticket bulunamadı.', 404);

    if (method.toUpperCase() === 'GET') return clone(ticket) as T;
    if (tail === '/reply') {
      const channel = input.channel === 'email' ? 'email' : 'whatsapp';
      const text = String(input.body || '').trim();
      if (!text) fail('VALIDATION', 'Yanıt boş olamaz.');
      ticket.messages.push({
        id: nextId('tm'),
        channel,
        author: 'agent',
        body: text,
        at: nowIso(),
        subject: channel === 'email' ? '[' + ticket.id + '] Yanıt' : undefined
      });
      ticket.status = 'pending';
      pushAudit('ticket.reply', ticket.id, 'ok', channel);
      return clone(ticket) as T;
    }
    if (tail === '/close') {
      ticket.status = 'resolved';
      pushAudit('ticket.close', ticket.id, 'ok', null);
      return clone(ticket) as T;
    }
    if (tail === '/assign') {
      ticket.assignee = input.userId ? String(input.userId) : null;
      return clone(ticket) as T;
    }
  }

  // ---------- SSS ----------
  if (route === 'GET /api/faqs') {
    return { items: clone(db.faqs), nextCursor: null } as T;
  }
  if (route === 'POST /api/faqs') {
    const question = String(input.question || '').trim();
    const answer = String(input.answer || '').trim();
    if (!question || !answer) fail('VALIDATION', 'Soru ve cevap zorunlu.');
    const created: Faq = {
      id: nextId('f'),
      question,
      answer,
      category: String(input.category || 'Genel'),
      tags: Array.isArray(input.tags) ? (input.tags as string[]) : [],
      active: input.active === undefined ? true : Boolean(input.active),
      updatedAt: nowIso(),
      hits: 0
    };
    db.faqs.unshift(created);
    pushAudit('faq.create', created.id, 'ok', question.slice(0, 40));
    return clone(created) as T;
  }

  const faqMatch = /^\/api\/faqs\/([^/]+)$/.exec(url.pathname);
  if (faqMatch) {
    const id = faqMatch[1];
    const faq = db.faqs.find((item) => item.id === id);
    if (!faq) fail('NOT_FOUND', 'SSS kaydı bulunamadı.', 404);
    if (method.toUpperCase() === 'DELETE') {
      db.faqs = db.faqs.filter((item) => item.id !== id);
      pushAudit('faq.delete', id, 'ok', null);
      return undefined as T;
    }
    if (method.toUpperCase() === 'PATCH') {
      if (input.question !== undefined) faq.question = String(input.question);
      if (input.answer !== undefined) faq.answer = String(input.answer);
      if (input.category !== undefined) faq.category = String(input.category);
      if (input.active !== undefined) faq.active = Boolean(input.active);
      if (input.tags !== undefined) faq.tags = input.tags as string[];
      faq.updatedAt = nowIso();
      pushAudit('faq.update', id, 'ok', null);
      return clone(faq) as T;
    }
  }

  // ---------- Kütüphane ----------
  if (route === 'GET /api/library') {
    return { items: clone(db.docs), nextCursor: null } as T;
  }
  if (route === 'POST /api/library/upload') {
    const name = String(input.name || 'belge.pdf');
    const size = Number(input.size || 0);
    if (!name.toLowerCase().endsWith('.pdf')) fail('VALIDATION', 'Yalnızca PDF yüklenebilir.');
    const created: Doc = {
      id: nextId('d'),
      name,
      sizeBytes: size,
      pages: 0,
      chunks: 0,
      status: 'processing',
      uploadedAt: nowIso(),
      indexedAt: null,
      proposedFaqs: 0
    };
    db.docs.unshift(created);
    pushAudit('library.upload', name, 'ok', Math.round(size / 1024) + ' KB');
    upsertDocReady(created);
    return clone(created) as T;
  }

  const docMatch = /^\/api\/library\/([^/]+)(\/.*)?$/.exec(url.pathname);
  if (docMatch) {
    const id = docMatch[1];
    const tail = docMatch[2] || '';
    const doc = db.docs.find((item) => item.id === id);
    if (!doc) fail('NOT_FOUND', 'Belge bulunamadı.', 404);
    if (method.toUpperCase() === 'DELETE') {
      db.docs = db.docs.filter((item) => item.id !== id);
      pushAudit('library.delete', doc.name, 'ok', null);
      return undefined as T;
    }
    if (tail === '/reindex') {
      doc.status = 'processing';
      doc.chunks = 0;
      doc.indexedAt = null;
      pushAudit('library.reindex', doc.name, 'ok', null);
      upsertDocReady(doc);
      return clone(doc) as T;
    }
    if (tail === '/confirm') {
      doc.proposedFaqs = 0;
      pushAudit('library.confirm', doc.name, 'ok', 'SSS adayları onaylandı');
      return clone(doc) as T;
    }
  }

  // ---------- AI ----------
  if (route === 'GET /api/ai/settings') return clone(db.ai) as T;
  if (route === 'PUT /api/ai/settings') {
    db.ai = { ...db.ai, ...(input as Partial<AiSettings>) };
    pushAudit('ai.settings_update', 'ai/settings', 'ok', null);
    return clone(db.ai) as T;
  }
  if (route === 'POST /api/ai/test') {
    const question = String(input.question || '');
    const normalized = question.toLocaleLowerCase('tr-TR');
    const hit = db.faqs.find((faq) =>
      faq.question
        .toLocaleLowerCase('tr-TR')
        .split(/\s+/)
        .filter((word) => word.length > 4)
        .some((word) => normalized.includes(word))
    );
    const confidence = hit ? 0.82 : 0.34;
    return {
      answer: hit
        ? hit.answer
        : 'Bu konuda emin değilim. Size en doğru bilgiyi verebilmek için e-posta adresinizi paylaşır mısınız?',
      confidence,
      sources: hit ? ['SSS: ' + hit.question] : [],
      wouldEscalate: confidence < db.ai.confidenceThreshold,
      latencyMs: 620 + Math.round(Math.random() * 400)
    } as T;
  }

  // ---------- Test modu ----------
  if (route === 'GET /api/test-mode') return clone(db.testMode) as T;

  if (route === 'POST /api/test-mode/preview') {
    const to = normalizePhone(String(input.to || ''));
    if (!to) fail('INVALID_PHONE', 'Numara geçersiz. Örnek: +90 551 888 58 86');
    const allowed = db.testMode.runtimeMode === 'LIVE' || activeAllowlist().includes(to);
    const result: TestPreviewResult = {
      allowed,
      reason: allowed ? 'İzinli numara' : 'İzin listesinde değil — guard engeller',
      guard: allowed ? 'ALLOW' : 'BLOCK',
      target: to
    };
    return result as T;
  }

  if (route === 'POST /api/test-mode/allowlist') {
    const phone = normalizePhone(String(input.phone || ''));
    if (!phone) fail('INVALID_PHONE', 'Numara geçersiz. Örnek: +90 551 888 58 86');
    if (db.testMode.allowlist.some((entry) => entry.phone === phone)) {
      fail('DUPLICATE', 'Bu numara zaten izin listesinde.');
    }
    db.testMode.allowlist.push({ phone, active: true, addedAt: nowIso() });
    pushAudit('test_mode.allowlist_add', phone, 'ok', null);
    return clone(db.testMode) as T;
  }

  if (route === 'DELETE /api/test-mode/allowlist') {
    const phone = normalizePhone(String(query.get('phone') || ''));
    if (!phone) fail('INVALID_PHONE', 'Numara geçersiz.');
    const entry = db.testMode.allowlist.find((item) => item.phone === phone);
    if (!entry) fail('NOT_FOUND', 'Numara izin listesinde değil.', 404);
    if (db.testMode.allowlist.length === 1) {
      fail('VALIDATION', 'Son izinli numara kaldırılamaz; test modu kapanmış olur.');
    }
    db.testMode.allowlist = db.testMode.allowlist.filter((item) => item.phone !== phone);
    pushAudit('test_mode.allowlist_remove', phone, 'ok', null);
    return clone(db.testMode) as T;
  }

  if (route === 'POST /api/test-mode/enter-live') {
    const phone = normalizePhone(String(input.phone || ''));
    const totp = String(input.totp || '').trim();
    if (!phone) fail('INVALID_PHONE', 'Canlıya geçiş için numarayı tam yazın.');
    if (totp !== '123456') fail('TOTP_INVALID', 'Doğrulama kodu hatalı. (demo kodu: 123456)');
    db.testMode.enabled = false;
    db.testMode.runtimeMode = 'LIVE';
    pushAudit('test_mode.enter_live', phone, 'ok', 'TOTP doğrulandı');
    return { state: clone(db.testMode), warnings: ['İlk 24 saat günlük limit 20 mesaj.'] } as T;
  }

  if (route === 'POST /api/test-mode/enter-test') {
    db.testMode.enabled = true;
    db.testMode.runtimeMode = 'TEST';
    pushAudit('test_mode.enter_test', 'runtime', 'ok', 'tek tık');
    return { state: clone(db.testMode) } as T;
  }

  // ---------- Audit ----------
  if (route === 'GET /api/admin/audit') {
    const action = String(query.get('action') || 'all');
    const result = String(query.get('result') || 'all');
    const search = String(query.get('q') || '');
    let items = db.audit.slice();
    if (action !== 'all') items = items.filter((item) => item.action.startsWith(action));
    if (result !== 'all') items = items.filter((item) => item.result === result);
    if (search) {
      items = items.filter(
        (item) => includesTr(item.actor, search) || includesTr(item.target, search) || includesTr(item.action, search)
      );
    }
    return { items: clone(items), nextCursor: null } as T;
  }

  // ---------- Ayarlar ----------
  if (route === 'GET /api/settings') return clone(panelConfig) as T;
  if (route === 'PUT /api/settings') {
    const patch = input as Partial<PanelConfig>;
    Object.assign(panelConfig, patch);
    pushAudit('settings.update', 'panel', 'ok', Object.keys(patch).join(','));
    return clone(panelConfig) as T;
  }
  if (route === 'POST /api/email/test') {
    return { ok: true, message: 'Test e-postası gönderildi: destek@softwareeni.com' } as T;
  }

  fail('NOT_FOUND', 'Mock uç bulunamadı: ' + route, 404);
}
